import type { ThemeJson } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';
import { CardEnvelopeReturnAddressLayout, CardPaperType } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';
import type { LayerData } from '@apps/card/routes/CardCustomizer/components/Layer.types';
import TRADITIONAL_FRONT from '@apps/card/routes/CardCustomizer/steps/Envelope/templates/traditional.front.return.json';
import { DEFAULT_ENVELOPE_THEME_ID } from '@apps/card/routes/CardCustomizer/steps/Envelope/envelopeTemplateUtils';
import type { CardDashboardStationeryTemplatesContainerQuery, StationeryDesignGalleryTemplateFragment } from './DesignsGallery.graphql.generated';
import { StationeryTemplateCategoryEnum, ThemeVariationType } from '@graphql/generated-base';
import type { DigitalDesignTemplateCategory, StationeryTemplate, ValidDigitalDesignApiData } from './DesignsGallery.types';
import { SAVE_THE_DATE_PHOTO_THEMES } from '@apps/admin/saveTheDate/constants';
import { PLACEHOLDER_TEMPLATE } from '@apps/card/routes/CardCustomizer/components/CardBackChoiceGroup';
import { isEmpty } from 'lodash-es';
import { getOrientation } from '@apps/card/utils';
import { prepareFacetOptions, mapToFacetOption } from './components/Filters/facetOptions';
import type { FacetOptionLocal } from './components/Filters/Filter.types';
import type { StationeryProduct, StationeryProductAttribute } from '@shared/utils/print-price/types';

const CATEGORIES_WITHOUT_BACK = [StationeryTemplateCategoryEnum.menu, StationeryTemplateCategoryEnum.napkin, StationeryTemplateCategoryEnum.sign];
export const CATEGORIES_WITHOUT_ENVELOPE = [...CATEGORIES_WITHOUT_BACK, StationeryTemplateCategoryEnum.enclosure];

// Called by DigitalCardCustomizer and usePremiumDigitalUpsell
export function applyDefaultBackTemplate(themeJSON: ThemeJson, category: StationeryTemplateCategoryEnum) {
  const shouldHaveBack = !CATEGORIES_WITHOUT_BACK.includes(category);
  if (shouldHaveBack && isEmpty(themeJSON.card.back)) {
    themeJSON.card.back = PLACEHOLDER_TEMPLATE;
  }
}

export function isPhotoTemplateThemeJSON(themeJson?: ThemeJson) {
  if (!themeJson) return false;

  return themeJson.card?.front?.layers?.some(layer => layer.type === 'image' && layer.editable) ?? false;
}

const getOptionIdsForAttribute = (attributes: ReadonlyArray<StationeryProductAttribute>, attributeId: string): string[] => {
  const attribute = attributes.find(attr => attr.attributeId === attributeId);
  return attribute?.attributeOptions.map(opt => opt.attributeOption.id) ?? [];
};

type ResetCardTemplateDefaultsTemplate = Readonly<{
  themeJSON: ThemeJson;
  products?: ReadonlyArray<StationeryProduct>;
}>;

type ResetCardTemplateDefaultsOptions = Readonly<{
  resetPaperType?: boolean;
}>;

// For MVP, we baked the default card back and envelope into every template.
// Moving forward, new templates will not contain a default card back and envelope.
// This fills/overwrites in the default data, so it doesn't matter what was baked into the template.
// This also sets the default paper type for photo templates to semi-gloss.
export function resetCardTemplateDefaults(template: ResetCardTemplateDefaultsTemplate, category?: StationeryTemplateCategoryEnum, options?: ResetCardTemplateDefaultsOptions) {
  const { themeJSON, products } = template;
  const { resetPaperType = true } = options ?? {};
  const shouldHaveBack = !CATEGORIES_WITHOUT_BACK.includes(category as StationeryTemplateCategoryEnum);
  themeJSON.card.back = shouldHaveBack ? PLACEHOLDER_TEMPLATE : undefined;
  themeJSON.card.envelope.layout = CardEnvelopeReturnAddressLayout.front;
  themeJSON.card.envelope.design = DEFAULT_ENVELOPE_THEME_ID;
  themeJSON.card.envelope.layers = TRADITIONAL_FRONT as LayerData[];

  if (resetPaperType && isPhotoTemplateThemeJSON(themeJSON)) {
    const productAttributes = products?.flatMap(p => p.attributes) ?? [];
    const paperTypeId = 'paperType';
    const availablePaperTypes = getOptionIdsForAttribute(productAttributes, paperTypeId);
    if (availablePaperTypes.includes(CardPaperType.semiGloss)) {
      themeJSON.customizations.paperType = CardPaperType.semiGloss;
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isValidDigitalDesign = (digitalDesign: any): digitalDesign is ValidDigitalDesignApiData => {
  return !!digitalDesign && !!digitalDesign.themeId && !!digitalDesign.thumbnailUrl && !!digitalDesign.theme?.name && !!digitalDesign.theme?.id;
};

const STATIONERY_TEMPLATE_CATEGORY_TO_THEME_VARIATION_TYPE: Partial<Record<StationeryTemplateCategoryEnum, ThemeVariationType | null>> = {
  saveTheDate: ThemeVariationType.savethedate,
  invitation: ThemeVariationType.invite,
  thankYou: null,
  holiday: null,
  enclosure: null,
};

const JOY_MARKETING_VENDOR = 'Joy';

type DesignServiceThemeData = {
  id: string;
  themeId: string;
  name: string;
  marketingData: CardDashboardStationeryTemplatesContainerQuery['ecardDesigns'][number]['marketingData'];
};

export const findMatchingMarketingData = (theme: DesignServiceThemeData, category: StationeryTemplateCategoryEnum) => {
  return theme.marketingData?.find(
    data =>
      // Ignoring partner designs for now
      data.vendor === JOY_MARKETING_VENDOR &&
      // `marketingData` is an array of related designs
      // eg. if `themeId` === 'arch_ii', marketingData will include 'arch_i', 'arch_ii', 'arch_iii'
      data.variantThemeId === theme.themeId &&
      // In modern ecard customizer, we're currently requesting all themes regardless of variation type --
      // which means that both save the date and invitations show the same set of themes.
      //
      // However, the theme tiles in the ecard customizer show a generic thumbnail (no text/content)
      // and this won't work in the card gallery because we need thumbnails with content preview.
      data.variationType === STATIONERY_TEMPLATE_CATEGORY_TO_THEME_VARIATION_TYPE[category],
  );
};

export const isFoilTemplate = (template: { themeId: string }) => {
  return template.themeId.endsWith('_foil');
};

const filterTemplatesByOrientation = <T extends Pick<StationeryDesignGalleryTemplateFragment, 'themeId' | 'widthInches' | 'heightInches' | 'category'>>(templates: T[]) => {
  // We want the lowest price to "win". Sort those last. When inserted into the map last one wins.
  const sortedTemplates = templates.slice();
  sortedTemplates.sort((a, b) => b.widthInches - a.widthInches);
  const templatesByOrientation = new Map(sortedTemplates.map(t => [`${t.themeId}@${getOrientation(t.widthInches, t.heightInches)}@${t.category}`, t]));

  return Array.from(templatesByOrientation.values());
};

export type StationeryTemplateWithProductIds = Omit<StationeryTemplate, 'products'> & { productIds: string[] };

export const processInitialQueryData = (data: CardDashboardStationeryTemplatesContainerQuery | undefined, category: StationeryTemplateCategoryEnum) => {
  const templates = (data?.stationeryTemplatesContainer.pagedResults.edges || []).map(t => t.node);

  const printTemplatesByTheme = new Map(templates.map(t => [t.themeId, t]));

  const templatesWithMatchingDigitalDesignMap = new Map<string, ValidDigitalDesignApiData>();
  const digitalOnlyDesigns: StationeryTemplateWithProductIds[] = [];

  if (category === StationeryTemplateCategoryEnum.saveTheDate) {
    // TODO: photo ecards should be sent from card_service
    // https://withjoy.atlassian.net/browse/PRNT-1107
    SAVE_THE_DATE_PHOTO_THEMES.forEach(theme => {
      const printableThemeConfig = printTemplatesByTheme.get(theme.themeId);
      if (printableThemeConfig) {
        templatesWithMatchingDigitalDesignMap.set(theme.themeId, {
          themeId: theme.themeId,
          thumbnailUrl: theme.thumbnailUrl,
          category,
          name: theme.name,
          isPremiumDesign: printableThemeConfig.premium,
        });
      }
    });
  }

  if (data?.ecardDesigns) {
    data.ecardDesigns.forEach(design => {
      const designThemeId = design.themeId;
      const designName = design.name;
      const digitalDesignConfig = findMatchingMarketingData(design, category);

      if (digitalDesignConfig) {
        const thumbnailUrl = digitalDesignConfig.imageUrl;
        const printableThemeConfig = printTemplatesByTheme.get(designThemeId);
        // Check if the digital design is also printable
        if (printableThemeConfig) {
          // if printable, track the digital design
          templatesWithMatchingDigitalDesignMap.set(designThemeId, {
            themeId: designThemeId,
            thumbnailUrl,
            name: designName,
            category: category as DigitalDesignTemplateCategory,
            isPremiumDesign: printableThemeConfig.premium,
          });
        } else {
          // Otherwise, store the digital design to be placed at the end of the list
          digitalOnlyDesigns.push({
            id: design.id,
            format: 'digital',
            themeId: designThemeId,
            category,
            approved: true,
            enabled: true,
            name: designName,
            thumbnailUrl: null,
            digitalDesignThumbnailUrl: thumbnailUrl,
            premium: false,
            tags: [],
            mappedTags: [],
            isFavorite: false,
            isFoil: false,
            widthInches: 5, // Legacy digital cards were 5x7
            heightInches: 7,
            productIds: [],
          });
        }
      }
    });
  }

  prepareFacetOptions(data?.stationeryTags);

  const processedInitialPrintableTemplates: Array<StationeryTemplateWithProductIds> =
    filterTemplatesByOrientation(templates).map(template => {
      const maybeDigitalDesignConfig = templatesWithMatchingDigitalDesignMap.get(template.themeId);

      // prettier-ignore
      const mappedTagsLocal: FacetOptionLocal[] = [
        template.tags.some(tag => tag === 'photo')    ? 'photo:with'    : 'photo:without',
        template.premium || !maybeDigitalDesignConfig ? 'price:premium' : 'price:basic',
        isFoilTemplate(template)                      ? 'foil:with'     : 'foil:without',
      ];

      return {
        ...template,
        // Mapping tags to composite codes for applying filters in `useApplyFiltersToTemplates`
        // prettier-ignore
        mappedTags: [
          ...mapToFacetOption(template.tags),
          `orientation:${getOrientation(template.widthInches, template.heightInches).toLowerCase()}`,
          ...mappedTagsLocal
        ],
        format: maybeDigitalDesignConfig ? 'paperAndDigital' : 'paper',
        digitalDesignThumbnailUrl: maybeDigitalDesignConfig?.thumbnailUrl,
        isFoil: isFoilTemplate(template),
      };
    }) || [];

  return [...processedInitialPrintableTemplates, ...digitalOnlyDesigns];
};
